import { FlagConfig } from './flag-catalog.types';
import { FlagKeyOf } from './flag-key';
import { FlagValueOf } from './runtime-config.port';

/**
 * The UI mechanism prefix — the namespace whose flags are read by the SPA at
 * bootstrap. Only flags under this prefix are served by `GET /feature-flags`;
 * everything else (`routes.*`, `wiring.*`, `behavior.*`) stays server-side.
 */
export const UI_FLAG_PREFIX = 'ui.' as const;

/**
 * Extract only the `ui.*` keys from a composed catalog type.
 *
 * This is the compile-time gate that ensures both the backend endpoint and the
 * frontend consumer agree on WHICH keys belong to the SPA surface — a single
 * source of truth derived from the same `FLAG_CATALOG` type. A backend that
 * hands out a `behavior.*` key, or a frontend that expects one, is a compile
 * error.
 */
export type UiFlagKeyOf<Catalog extends Readonly<Record<string, FlagConfig>>> = {
    [Key in FlagKeyOf<Catalog>]: Key extends `ui.${string}` ? Key : never;
}[FlagKeyOf<Catalog>];

/**
 * The runtime shape the `GET /feature-flags` response carries: for each `ui.*`
 * key, its resolved value.
 *
 * The backend controller builds this from the port; the frontend consumes it.
 * Typing both sides against the same derived shape keeps them in sync at compile
 * time — a new `ui.*` flag in the catalog appears in both places automatically.
 */
export type UiFlagValues<Catalog extends Readonly<Record<string, FlagConfig>>> = {
    readonly [Key in UiFlagKeyOf<Catalog>]: FlagValueOf<Catalog[Key]>;
};

/**
 * Extract the `ui.*` flag keys from a composed catalog at runtime.
 *
 * Used by the backend controller to iterate only the UI-surface keys and by the
 * frontend to enumerate which keys must be present / fall back.
 */
export function uiFlagKeysOf<Catalog extends Readonly<Record<string, FlagConfig>>>(
    catalog: Catalog
): UiFlagKeyOf<Catalog>[] {
    return (Object.keys(catalog) as FlagKeyOf<Catalog>[]).filter((key): key is UiFlagKeyOf<Catalog> =>
        key.startsWith(UI_FLAG_PREFIX)
    );
}

/**
 * Compute the catalog default value for one flag, used when the runtime config
 * source is unreachable at bootstrap.
 *
 * Boolean: returns the catalog's `defaultValue`.
 * Enum: returns the catalog's `defaultValue`.
 * Variant: returns the catalog's `defaultVariant`.
 */
export function catalogDefault(config: FlagConfig): boolean | string {
    if (config.type === 'boolean') {
        return config.defaultValue;
    }
    if (config.type === 'enum') {
        return config.defaultValue;
    }
    if (config.type === 'variant') {
        return config.defaultVariant;
    }
    return false;
}

/**
 * Build the catalog-default map for every `ui.*` flag. The frontend uses this
 * as the fallback when the bootstrap fetch fails.
 */
export function uiFlagDefaults<Catalog extends Readonly<Record<string, FlagConfig>>>(
    catalog: Catalog
): UiFlagValues<Catalog> {
    const defaults: Record<string, boolean | string> = {};
    for (const key of uiFlagKeysOf(catalog)) {
        defaults[key] = catalogDefault(catalog[key]);
    }
    return defaults as UiFlagValues<Catalog>;
}
